import { cachedCall } from '@/services/screenCache'
import {
  ATYRAU_ALL,
  ATYRAU_DISTRICTS,
  ATYRAU_DISTRICT_SHORT,
} from '@/constants/atyrauDistricts'
import {
  fetchNcipDictionaries,
  fetchNcipProjects,
  fetchNcipSummary,
  type NcipBreakdown,
} from '@/services/ncipApi'

/**
 * Сводка «Бизнес и инвесторы» для акима области.
 *
 * ИСТОЧНИК — ТОЛЬКО НЦИП (`/ncip/*`), те же ручки, что у сайта: плитка
 * «Предпринимательство и инвесторы» (`GroupBusiness.vue`) и домен «Бизнес»
 * (`ScDomainBusiness.vue`). Цифры по области совпадают с плиткой один в один.
 *
 * РАЙОН ФИЛЬТРУЕТ СЕРВЕР (`?district=`), как домен сайта. Раньше планшет
 * скачивал весь список и отбирал проекты у себя по нормализованному имени —
 * это вторая реализация того же правила, и расходилась бы с сайтом, стоит
 * реестру вырасти за страницу.
 *
 * ЧЕГО ЗДЕСЬ БОЛЬШЕ НЕТ (29.09.2026):
 * — «Сигналов бизнеса»: ручек `/business-*` у бэкенда нет, `signalService`
 *   отдавал демо-набор, и в режиме по умолчанию вымышленная плитка «1 847»
 *   стояла среди настоящих KPI;
 * — «Динамики портфеля»: реестр — срез, ряда во времени у него нет, а график
 *   склеивал стадии с отраслями и выглядел динамикой;
 * — «Под риском / пауза»: считался по полю стадии, которое у 135 из 144
 *   проектов пустое, и был всегда 0. Вместо него — `withProblems`, статус
 *   реализации «С проблемами».
 */

export type BusinessInvestorsAkimKpi = {
  id: string
  label: string
  value: string | number
  delta?: string
  positive?: boolean
  none?: boolean
  hint?: string
}

export type BusinessInvestorsChartRow = {
  name: string
  value: number
  value2?: number
  short?: string
}

export type BusinessInvestorsAkimDashboard = {
  territoryLabel: string
  kpis: BusinessInvestorsAkimKpi[]
  byIndustry: BusinessInvestorsChartRow[]
  /** Ход реализации: В реализации / В эксплуатации / С проблемами. */
  byRealization: BusinessInvestorsChartRow[]
  byDistrict: BusinessInvestorsChartRow[]
  byCountry: BusinessInvestorsChartRow[]
  topProjects: Array<{
    id: number
    name: string
    district: string
    industry: string
    costMlnKzt: number
    jobs: number
    realizationStatus: string
  }>
  sourceNote: string
}

function formatMoneyMln(mln: number): string {
  if (!Number.isFinite(mln) || mln <= 0) return '—'
  if (mln >= 1_000_000) return `${(mln / 1_000_000).toFixed(1)} трлн ₸`
  if (mln >= 1_000) return `${(mln / 1_000).toFixed(1)} млрд ₸`
  return `${Math.round(mln).toLocaleString('ru-RU')} млн ₸`
}

/** Нормализация имён районов НЦИП → наш справочник. */
export function normalizeNcipDistrict(raw?: string | null): string {
  if (!raw) return 'Район не указан'
  const t = raw.trim()

  // НЦИП: «Атырау Г.А.» / «г.Атырау» / просто «Атырау»
  if (/атырау/i.test(t) && /(г\.?\s*а\.?|город|га\b)/i.test(t)) {
    return 'г. Атырау'
  }
  if (/^атырау$/i.test(t)) return 'г. Атырау'

  // НЦИП пишет «Кзылкогинский» без «ы» после К
  if (/к[ы]?зылкогин/i.test(t)) return 'Кызылкогинский район'

  const hit = ATYRAU_DISTRICTS.find((d) => {
    if (d === t) return true
    const short = d.replace(/ район$/i, '')
    const rawShort = t.replace(/ район$/i, '')
    return (
      t.toLowerCase().includes(short.toLowerCase()) ||
      d.toLowerCase().includes(rawShort.toLowerCase())
    )
  })
  return hit ?? t
}

/*
 * Наш район → значение района в реестре. Справочник собран из тех же данных и
 * от фильтров не зависит, поэтому берётся один раз за сеанс.
 */
let districtValues: Promise<Map<string, string>> | null = null

function ncipDistrictValue(district: string): Promise<string | undefined> {
  if (!districtValues) {
    districtValues = fetchNcipDictionaries()
      .then((dict) => new Map((dict.districts ?? []).map((d) => [normalizeNcipDistrict(d.value), d.value])))
      .catch((error) => {
        districtValues = null
        throw error
      })
  }
  return districtValues.then((map) => map.get(district))
}

function ranked(rows: NcipBreakdown[] | undefined, limit = 8): BusinessInvestorsChartRow[] {
  return [...(rows ?? [])]
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map((r) => ({ name: r.value || 'Не указано', value: r.count, value2: Math.round(Number(r.costMlnKzt) || 0) }))
}

async function fetchBusinessInvestorsAkimDashboardRaw(params: {
  district?: string
}): Promise<BusinessInvestorsAkimDashboard> {
  const district = params.district ?? ATYRAU_ALL
  const isOblast = district === ATYRAU_ALL
  const territoryLabel = isOblast ? 'Атырауская область' : district

  // Района нет в реестре — проектов в нём нет; не уходим на всю область молча.
  const ncipDistrict = isOblast ? undefined : await ncipDistrictValue(district)
  if (!isOblast && !ncipDistrict) {
    throw new Error(`В реестре НЦИП нет проектов по району «${district}»`)
  }

  const [summary, top] = await Promise.all([
    fetchNcipSummary({ district: ncipDistrict }),
    fetchNcipProjects({ limit: 8, district: ncipDistrict, sort: '-cost' }),
  ])

  const byDistrictMap = new Map(
    (summary.byDistrict ?? []).map((r) => [normalizeNcipDistrict(r.value), r]),
  )
  const byDistrict = ATYRAU_DISTRICTS.map((name) => {
    const row = byDistrictMap.get(name)
    return {
      name,
      short: ATYRAU_DISTRICT_SHORT[name] ?? name,
      value: row?.count ?? 0,
      value2: Math.round(Number(row?.costMlnKzt) || 0),
    }
  }).sort((a, b) => b.value - a.value)

  const problems = Number(summary.withProblems) || 0

  return {
    territoryLabel,
    kpis: [
      {
        id: 'projects',
        label: 'Инвестпроектов',
        value: summary.projects,
        hint: territoryLabel,
        positive: true,
      },
      {
        id: 'cost',
        label: 'Стоимость портфеля',
        value: formatMoneyMln(summary.costMlnKzt),
        hint: `иностр. ${formatMoneyMln(summary.foreignFundsMlnKzt)}`,
        positive: true,
      },
      {
        /*
         * Только постоянные, как на сайте («Плановых постоянных рабочих мест»):
         * с временными (стройка) число было втрое больше и не сходилось с плиткой.
         */
        id: 'jobs',
        label: 'Постоянных рабочих мест',
        value: summary.jobsPermanent.toLocaleString('ru-RU'),
        hint: `план · временных ${summary.jobsTemporary.toLocaleString('ru-RU')}`,
        positive: true,
      },
      {
        id: 'problems',
        label: 'С проблемами',
        value: problems,
        delta: problems > 0 ? 'статус реализации «С проблемами»' : 'нет',
        positive: problems === 0,
        none: problems === 0,
      },
    ],
    byIndustry: ranked(summary.byIndustry),
    byRealization: ranked(summary.byRealizationStatus),
    byDistrict,
    byCountry: ranked(summary.byInvestorCountry),
    topProjects: (top.items ?? top.data ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      district: normalizeNcipDistrict(p.district),
      industry: p.industry ?? '—',
      costMlnKzt: Number(p.costMlnKzt) || 0,
      jobs: (Number(p.jobsPermanent) || 0) + (Number(p.jobsTemporary) || 0),
      realizationStatus: p.realizationStatus ?? '—',
    })),
    sourceNote: 'НЦИП — реестр инвестпроектов области; те же ручки, что у сайта (`/ncip/summary`, `/ncip/projects`).',
  }
}

/**
 * Экран целиком — в `screenCache` на час: собранная сводка весит килобайты и
 * переживает перезагрузку вкладки, повторное открытие — без запросов. Сырые
 * ответы при этом живут только в памяти (`api.ts`). Кнопка «Обновить» сбрасывает
 * ключ через `invalidateScreenCache`.
 */
export function fetchBusinessInvestorsAkimDashboard(params: {
  district?: string
}): Promise<BusinessInvestorsAkimDashboard> {
  return cachedCall(`business/dashboard:${params.district ?? 'all'}`, () => fetchBusinessInvestorsAkimDashboardRaw(params))
}
